/* eslint complexity: 0 */

/**
 * Reducer for spaces (including space members & billing info).
 */

import _merge from "lodash/merge";
import _union from "lodash/union";
import _mergeWith from "lodash/mergeWith";
import _cloneDeep from "lodash/cloneDeep";

import * as actionTypes from "../../actions/actionTypes";

const spaceReducer = (state = {}, action) => {
  let spaces;
  try {
    spaces = action.response.entities.spaces;
  } catch (error) {
    // That's ok
  }

  switch (action.type) {
    case actionTypes.UPDATE_SPACE_NOTIFICATIONS_SUCCESS: {
      const {
        spaceId,
        response: {
          result,
          entities: { spaceMembers },
        },
      } = action;
      const space = state[spaceId];

      if (!space) return state;

      return {
        ...state,
        [spaceId]: {
          ...space,
          access: {
            ...space.access,
            notificationPrefs: spaceMembers[result].notificationPrefs,
          },
        },
      };
    }

    // IDEA: This count updating is duplicated here and in spaceMembers
    // Maybe we could conolidate to one source of truth
    case actionTypes.DELETE_SPACE_MEMBERS_SUCCESS: {
      const { spaceId } = action,
        space = state[spaceId],
        counts = { ...space.counts, users: space.counts.users - 1 },
        updatedSpace = {
          ...space,
          counts,
        };

      return { ...state, [spaceId]: updatedSpace };
    }
    case actionTypes.INVITE_MEMBERS_SUCCESS: {
      const { spaceId } = action,
        stateSpace = state[spaceId],
        sendCount = action.response.result.invitations.filter(inv => inv.success).length,
        counts = {
          ...stateSpace.counts,
          invitations: stateSpace.counts.invitations + sendCount,
        };

      return { ...state, [spaceId]: { ...stateSpace, counts } };
    }
    case actionTypes.DELETE_INVITATION_SUCCESS: {
      const { spaceId } = action,
        stateSpace = state[spaceId],
        counts = { ...stateSpace.counts, invitations: stateSpace.counts.invitations - 1 };

      return { ...state, [spaceId]: { ...stateSpace, counts } };
    }
    case actionTypes.FETCH_API_TOKENS_SUCCESS: {
      const { apiTokens } = action.response.entities || {};
      if (!apiTokens) return state;

      const spaceId = action.spaceId;
      return _merge({}, state, {
        [spaceId]: { apiTokens: Object.values(apiTokens) },
      });
    }
    case actionTypes.CREATE_API_TOKEN_SUCCESS:
    case actionTypes.DELETE_API_TOKEN_SUCCESS:
    case actionTypes.EDIT_API_TOKEN_SUCCESS: {
      const editedToken = action.response.entities.apiTokens[action.response.result],
        spaceId = editedToken.spaceId;

      let tokens = [...(state[spaceId].apiTokens || [])];
      if (editedToken.status === "deleted") {
        tokens = tokens.filter(t => t.token !== editedToken.token);
      } else if (tokens.find(t => t.token === editedToken.token)) {
        tokens = tokens.map(t => (t.token === editedToken.token ? editedToken : t));
      } else {
        tokens.push(editedToken);
      }

      return {
        ...state,
        [spaceId]: { ...state[spaceId], apiTokens: tokens },
      };
    }
    case actionTypes.EDIT_SPACE_THEME_SUCCESS: {
      const { id, response } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          theme: {
            ...response.entities.spaces[id].theme,
          },
        },
      };
    }
    case actionTypes.LEAVE_SPACE_SUCCESS: {
      const deletedSpaceId = action.id || action.spaceId;

      const newState = {};
      Object.keys(state).forEach(spaceId => {
        if (spaceId !== deletedSpaceId) {
          newState[spaceId] = state[spaceId];
        }
      });

      return newState;
    }
    case actionTypes.FETCH_CHARGE_PREVIEW_SUCCESS: {
      const spaceId = action.spaceId;
      return _merge({}, state, { [spaceId]: { account: { card: action.response.card } } });
    }
    case actionTypes.FETCH_BILLING_HISTORY: {
      const { spaceId } = action,
        space = state[spaceId];
      return {
        ...state,
        [spaceId]: {
          ...space,
          account: { ...space.account, requestStatus: "loading" },
        },
      };
    }

    case actionTypes.FETCH_BILLING_HISTORY_FAILURE: {
      const { spaceId } = action,
        space = state[spaceId];
      return { ...state, [spaceId]: { ...space, account: { requestStatus: "failure" } } };
    }
    case actionTypes.FETCH_BILLING_HISTORY_SUCCESS: {
      const { spaceId } = action,
        space = state[spaceId],
        { account } = action.response.entities.spaces[action.spaceId];
      return {
        ...state,
        [spaceId]: {
          ...space,
          account: { ...account, requestStatus: "success" },
        },
      };
    }
    case actionTypes.EDIT_INVOICE_SUCCESS: {
      const { spaceId } = action,
        space = state[spaceId],
        charges = state[spaceId].account.charges.map(charge =>
          charge.id === action.response.result.id ? action.response.result : charge
        );

      return {
        ...state,
        [spaceId]: {
          ...space,
          account: {
            ...space.account,
            charges,
            requestStatus: "success",
          },
        },
      };
    }
    case actionTypes.REPLACE_CREDIT_CARD_SUCCESS: {
      const { spaceId } = action,
        space = state[spaceId],
        nextSpace = action.response.entities.spaces[action.spaceId],
        { charges } = space.account;
      return {
        ...state,
        [spaceId]: {
          ...space,
          account: {
            ...nextSpace.account,
            charges,
            requestStatus: "success",
          },
        },
      };
    }
    case actionTypes.POLL_FOR_DUPLICATE_KIT_SUCCESS: {
      const { sourceId, spaceId } = action,
        newKits = Object.keys(action.response.entities.kits),
        space = state[spaceId];
      let kits = space.kits || [];

      const index = kits.indexOf(sourceId);
      if (index >= 0) {
        kits = [...kits];
        kits.splice(index + 1, 0, ...newKits);
      } else {
        kits = [...kits, ...newKits];
      }
      return {
        ...state,
        [spaceId]: {
          ...space,
          kits: kits,
        },
      };
    }
    case actionTypes.CREATE_KIT_SUCCESS: {
      const { spaceId } = action,
        kits = Object.keys(action.response.entities.kits);
      return _mergeWith({}, state, { [spaceId]: { kits } }, (objValue, srcValue, key) => {
        if (key === "kits") {
          return _union(objValue, srcValue);
        }
      });
    }
    case actionTypes.CREATE_KIT_COLLECTION_SUCCESS: {
      const {
        spaceId,
        response: { result },
      } = action;

      return {
        ...state,
        [spaceId]: {
          ...state[spaceId],
          kitCollections: [...state[spaceId].kitCollections, result],
        },
      };
    }
    case actionTypes.DELETE_KIT_COLLECTION_SUCCESS: {
      const {
        response: { result },
      } = action;

      const { spaceId } = action.response.entities.kitCollections[result];
      const newCols = [...state[spaceId].kitCollections];
      newCols.splice(state[spaceId].kitCollections.indexOf(result), 1);

      return {
        ...state,
        [spaceId]: {
          ...state[spaceId],
          kitCollections: newCols,
        },
      };
    }
    case actionTypes.FETCH_FIGMA_INTGERATION_STATUS_SUCCESS:
    case actionTypes.UPDATE_FIGMA_API_KEY_SUCCESS:
    case actionTypes.DISABLE_FIGMA_INTEGRATION_SUCCESS: {
      const { response, spaceId } = action,
        isDeleted = response.auth?.status === "deleted",
        newData = {
          [spaceId]: {
            integrations: {
              figma: isDeleted
                ? { auth: null, teams: [], error: null }
                : { ...response, error: null },
            },
          },
        };
      return _merge({}, state, newData);
    }
    case actionTypes.FETCH_FIGMA_INTGERATION_STATUS_FAILURE: {
      const { error, spaceId } = action,
        newData = {
          [spaceId]: {
            integrations: {
              figma: {
                error,
                auth: null,
                teams: null,
              },
            },
          },
        };
      return _merge({}, state, newData);
    }
    case actionTypes.ADD_FIGMA_TEAM_SUCCESS:
    case actionTypes.REMOVE_FIGMA_TEAM_SUCCESS: {
      const {
        response: { auth, team },
        spaceId,
      } = action;
      const space = _cloneDeep(state[spaceId]);
      // Ensure there is a integrations.figma object
      _merge(space, { integrations: { figma: {} } });
      let teams = space.integrations.figma.teams || [];
      if (team.status === "deleted") {
        teams = teams.filter(t => t.team_id !== team.team_id);
      } else if (teams.some(t => t.team_id === team.team_id)) {
        teams = teams.map(t => (t.team_id === team.team_id ? team : t));
      } else {
        teams.push(team);
      }
      space.integrations.figma.teams = teams;
      if (auth) {
        // auth comes back when removing a team in case the removed team
        // was putting the API key in an error state
        space.integrations.figma.auth = auth;
      }
      return {
        ...state,
        [spaceId]: space,
      };
    }
    case actionTypes.FETCH_JOINABLE_SPACES_SUCCESS:
      // These are preeviews that we don't want to put in global state.
      return state;
    default: {
      if (spaces) {
        return _mergeWith({}, state, spaces, (objValue, srcValue, key) => {
          if (["features", "permissions", "joinSettings"].includes(key)) {
            return srcValue;
          }
          if (key === "kits") {
            return _union(srcValue, objValue);
          }
          if (key === "avatars") {
            return srcValue ?? objValue;
          }
        });
      }
      return state;
    }
  }
};

export default spaceReducer;
